import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { Accordion } from 'components/molecules/Accordion';
import { useTranslations } from 'hooks/useTranslations';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { useAncillaries } from 'pages/AncillariesPage/hooks';
import { EDictionaryTypes } from 'models/dictionary';
import { useLang } from 'models/langContext';
import { ERouteLinks } from 'models/route';
import { useAlert } from 'models/alertContext';
import { Alert, EAlertVariants, IAlert } from 'components/atoms/Alert';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import Button from 'components/atoms/Button';
import { Chips } from 'components/atoms/Chips';
import { getFieldsByAccessoriesType } from 'pages/AncillariesPage/helpers';
import {
  EAncillaryState,
  EArtAncillaryTypeName,
  EArtAncillaryTypeNameLowerCase,
} from 'constants/ArtAccessoryType';
import { EPermission } from 'models/permissions';
import { usePermission } from 'hooks/usePermission';
import { ECustomFieldObjectType, ECustomFieldType } from 'models/customField';
import { clearAlertTimeout, EAlertTime, formatDate } from 'utils/date';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { IAncillaryDetailsCustomfield } from 'models/ancillaries';
import { emptyFieldHolder } from 'components/molecules/CardList';
import Link from '@mui/material/Link';
import { ActionTypes } from 'state/actions/alert';
import { EFirearmStateId } from 'models/firearm';
import { renderDataColorByValues } from 'components/molecules/Table/helpers';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { StyledLegalLink } from 'components/atoms/StyledLegalLink';
import { getLegalEntityType } from 'components/organisms/RegisteredFirearmDetails';
import { usePrivilegesValidation } from 'utils/privilegesValidations';
import { EState, statesWithoutMovedTransaction } from 'utils/transactionUtils';
import { Asset } from 'utils/assets';
import { AccessoryInformationContainer, AccessoryInformationTitle } from './styles';
import { AccessoriesTransactions } from './AccessoriesTransactions';
import { AccessoriesComments } from './AccessoriesComments';
import { AccessoriesAttachments } from './AccessoriesAttachments';
import ImagesGallery from 'components/organisms/ImagesGallery';
import { EModuleType } from 'components/organisms/ImagesGallery/helper';
import ImagesGalleryRadioButton from 'components/organisms/ImagesGalleryRadioButton';
import { EGalleryTypeAncillary } from 'components/organisms/ImagesGalleryRadioButton/helpers';
import { EMarkingTypes } from 'constants/Markings';
import { findInDictionary } from 'helpers/dictionary';

export enum EAccessoryLabels {
  accessoryGeneralInformation = 'accessoryDetails.accessoryGeneralInformation.header',
  accessoryDetailedInformation = 'accessoryDetails.accessoryDetailedInformation.header',
  accessoryMarkings = 'accessoryDetails.accessoryMarkings.header',
  accessoryType = 'accessoryDetails.accessoryType.label',
  ancillaryType = 'ancillaryDetails.ancillaryType.label',
  artId = 'accessoryDetails.artId.label',
  hostType = 'accessoryDetails.hostType.label',
  manufacturer = 'accessoryDetails.accessoryManufacturer.label',
  productName = 'accessoryDetails.productName.label',
  calibre = 'accessoryDetails.calibre.label',
  composition = 'accessoryDetails.composition.label',
  proofHouse = 'accessoryDetails.proofMark.label',
  firecycleID = 'accessoryDetails.firecycleID.label',
  serialNumber = 'accessoryDetails.serialNumber.label',
  componentState = 'accessoryDetails.componentState.label',
  exhibitNumber = 'accessoryDetails.exhibitNumber.label',
  currentOwner = 'accessoryDetails.currentOwner.label',
  currentKeeper = 'accessoryDetails.currentKeeper.label',
  magnification = 'accessoryDetails.magnification.label',
  utility = 'accessoryDetails.utility.label',
  reticle = 'accessoryDetails.reticle.label',
  lightcolour = 'accessoryDetails.lightcolour.label',
  capacity = 'accessoryDetails.capacity.label',
  manufacturedDate = 'accessoryDetails.manufacturedDate.label',
  importMarking = 'accessoryDetails.importMarking.label',
  exportMarking = 'accessoryDetails.exportMarking.label',
  deactivationMarking = 'accessoryDetails.deactivationMarking.label',
  confiscationMarking = 'accessoryDetails.confiscationMarking.label',
  surplusSaleMarking = 'accessoryDetails.surplusSaleMarking.label',
  appliedMarking = 'accessoryDetails.appliedMarking.label',
  marking = 'transactionDetails.ancillaryDetails.accordion.marking.label',
  yearOfTransfer = 'transactionDetails.ancillaryDetails.accordion.yearOfTransfer.label',
  yearOfConfiscation = 'transactionDetails.ancillaryDetails.accordion.yearOfConfiscation.label',
}

const DetailAccessories = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslations();
  const { selectedLanguage, defaultLanguage } = useLang();
  const navigate = useNavigate();
  const { nextPageAlert, clearAlert, transactionAlert, ancillaryAlert, setAlert } = useAlert();
  const {
    loadDictionaries,
    accessoryDetail,
    getAccessoryDetail,
    artAccessoryManufacturerFlashEliminator,
    artAccessoryManufacturerLaserLightModule,
    artAccessoryManufacturerMagazine,
    artAccessoryManufacturerOpticalSight,
  } = useAncillaries();
  const [accessFields, setAccessFields] = useState<string[]>([]);
  const [isAllExpanded, setIsAllExpanded] = useState<boolean>(false);
  const { hasPermissions } = usePermission();
  const { shortDateFormat } = useGlobalProperty();
  const { getCustomField, customFieldsAncillaries } = useLegalEntities();
  const { isAnyLicenceAuthorityValid } = usePrivilegesValidation();
  const [galleryType, setGalleryType] = useState<EGalleryTypeAncillary>(
    EGalleryTypeAncillary.ART,
  );
  const [showMarkings, setShowMarkings] = useState(false);

  useEffect(() => {
    if (accessoryDetail) {
      if (accessoryDetail.typeUniqueId) {
        const fields = getFieldsByAccessoriesType(accessoryDetail.typeUniqueId);
        setAccessFields(fields);
      } else {
        setAccessFields([]);
      }
    }
  }, [accessoryDetail]);

  useEffect(() => {
    (async () => {
      if (id) {
        await getAccessoryDetail(id);
      }
    })();
  }, [id]);

  useEffect(
    () => {
      clearAlertTimeout(clearAlert, ActionTypes.CLEAR_SAME_PAGE_ALERT, EAlertTime.SHORT);
      clearAlertTimeout(clearAlert, ActionTypes.CLEAR_NEXT_PAGE_ALERT, EAlertTime.SHORT);
      clearAlertTimeout(clearAlert, ActionTypes.CLEAR_TRANSACTION_ALERT, EAlertTime.SHORT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    (async () => {
      await getCustomField(ECustomFieldObjectType.AN_ACCESSORY);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_ANCILLARY_ALERT);
    },
    [],
  );

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([
        EDictionaryTypes.ArtAncillaryOptions,
        EDictionaryTypes.ArtAccessoryManufacturerFlashEliminator,
        EDictionaryTypes.ArtAccessoryManufacturerLaserLightModule,
        EDictionaryTypes.ArtAccessoryManufacturerMagazine,
        EDictionaryTypes.ArtAccessoryManufacturerOpticalSight,
      ]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const licenceAuthorityValid =
    !!accessoryDetail && isAnyLicenceAuthorityValid(accessoryDetail.licenceAuthorityIds);

  const showField = (fieldName: string) => accessFields.includes(fieldName);

  const renderCustomFieldData = (customField: IAncillaryDetailsCustomfield) => {
    if (customField.customFieldType === ECustomFieldType.BOOL) {
      if (customField.value === null) {
        return '-';
      }
      return customField.value
        ? t('customFields.value.yes.label')
        : t('customFields.value.no.label');
    }
    if (customField.customFieldType === ECustomFieldType.DATE) {
      return formatDate(customField.value, shortDateFormat);
    }
    return customField.value || '-';
  };

  const navigateToMap = () => {
    navigate(ERouteLinks.RegisteredAncillariesMap, {
      state: {
        ancillaryUniqueId: accessoryDetail?.uniqueId,
        ancillaryType: EArtAncillaryTypeName.ACCESSORY.toLowerCase(),
        legalityUniqueId: accessoryDetail?.legalityUniqueId,
        navigateBack: `${ERouteLinks.ViewAccessories.replace(':id', id as string)}`,
        showAdvanced: true,
        showLegalityBar: false,
        breadcrumbs: [
          {
            label: t('module.ancillaries.name'),
            route: ERouteLinks.Ancillaries,
          },
          {
            label: t('module.accessories.name'),
            route: `${ERouteLinks.Ancillaries}?expand=accessories`,
          },
          {
            label: t('accessoryDetails.accordion.title.label'),
            route: `${ERouteLinks.ViewAccessories.replace(':id', id as string)}`,
          },
          t('ancillaries.ancillariesLocation.showOnMap.label'),
        ],
      },
    });
  };

  const handleRedirectToEditPageButton = (): (() => void) => {
    if (
      customFieldsAncillaries?.length &&
      accessoryDetail?.stateUniqueId !== EFirearmStateId.DISABLED
    ) {
      return () =>
        navigate(
          ERouteLinks.EditAccessories.replace(':id', accessoryDetail?.uniqueId as string),
        );
    }
    if (accessoryDetail?.stateUniqueId === EFirearmStateId.DISABLED) {
      return () =>
        setAlert(ActionTypes.SET_ANCILLARY_ALERT, {
          text: t('ancillary.disabled.edit.info'),
          variant: EAlertVariants.error,
        } as IAlert);
    }
    return () =>
      setAlert(ActionTypes.SET_ANCILLARY_ALERT, {
        text: t('general.noAvaibleDataToEdit.label'),
        variant: EAlertVariants.warning,
      } as IAlert);
  };

  const handleRedirectToMovedTransaction = () => {
    if (statesWithoutMovedTransaction.includes(accessoryDetail?.stateUniqueId as EState))
      return;

    if (accessoryDetail?.groupName) {
      setAlert(ActionTypes.SET_TRANSACTION_ALERT, {
        text: t('ancillary.transaction.addTransaction.text'),
        variant: EAlertVariants.warning,
      } as IAlert);
      clearAlert(ActionTypes.SET_SAME_PAGE_ALERT);
      return;
    }

    navigate(ERouteLinks.MoveTransaction, {
      state: {
        assetUniqueId: accessoryDetail?.uniqueId,
        asset: Asset.ANCILLARY,
        isGroup: false,
        searchAddressesFor: accessoryDetail?.keeperId || accessoryDetail?.ownerId,
        navigateBack: `${ERouteLinks.ViewAccessories.replace(':id', id as string)}`,
        breadcrumbs: [
          {
            label: t('module.ancillaries.name'),
            route: ERouteLinks.Ancillaries,
          },
          {
            label: t('module.accessories.name'),
            route: `${ERouteLinks.Ancillaries}?expand=accessories`,
          },
          {
            label: t('accessoryDetails.accordion.title.label'),
            route: `${ERouteLinks.ViewAccessories.replace(':id', id as string)}`,
          },
          t('transactions.movedTransaction.breadcrumb.label'),
        ],
      },
    });
  };

  return (
    <div>
      <Breadcrumbs
        items={[
          {
            label: t('module.ancillaries.name'),
            route: ERouteLinks.Ancillaries,
          },
          {
            label: t('module.accessories.name'),
            route: `${ERouteLinks.Ancillaries}?expand=accessories`,
          },
          t('accessoryDetails.accordion.title.label'),
        ]}
      />
      {accessoryDetail && (
        <Grid sx={{ gap: 3 }}>
          <DetailsTemplate
            title={accessoryDetail?.type}
            pageType={EDetailsPageTypes.view}
            redirectToShowOnMap={
              ![
                EAncillaryState.DESTROYED,
                EAncillaryState.DEPLETED,
                EAncillaryState.DISABLED,
              ].includes(accessoryDetail.stateUniqueId as EAncillaryState) &&
              accessoryDetail?.latitude
                ? navigateToMap
                : undefined
            }
            showOnMapButtonId="accessory-show-on-map-button"
            editPermissions={[EPermission.EDIT_ANCILLARY]}
            redirectToEditView={
              licenceAuthorityValid && accessoryDetail.customFields
                ? handleRedirectToEditPageButton()
                : undefined
            }
            redirectToDisableView={
              accessoryDetail.stateUniqueId === EAncillaryState.DISABLED ||
              !hasPermissions([EPermission.DISABLE_ANCILLARY]) ||
              !licenceAuthorityValid
                ? undefined
                : () =>
                    navigate(
                      `${ERouteLinks.DisableAncillary.replace(
                        ':id',
                        accessoryDetail.uniqueId,
                      ).replace(':type', EArtAncillaryTypeName.ACCESSORY.toLowerCase())}`,
                    )
            }
            redirectToMovedTransaction={handleRedirectToMovedTransaction}
            hideMovedTransaction={
              !licenceAuthorityValid ||
              statesWithoutMovedTransaction.includes(
                accessoryDetail?.stateUniqueId as EState,
              ) ||
              !hasPermissions(EPermission.ADD_TRANSACTION)
            }
            showEditButton={customFieldsAncillaries.length > 0}
          >
            <Grid
              container
              sx={{
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                justifyContent: 'center',
                gap: '20px',
              }}
            >
              <Grid container sx={{ gap: '12px', alignContent: 'flex-start', width: '270px' }}>
                <ImagesGalleryRadioButton
                  setGalleryType={setGalleryType}
                  galleryType={galleryType}
                  moduleType={EModuleType.ANCILLARY}
                />

                <ImagesGallery
                  key={'ancillary_details_gallery'}
                  moduleType={EModuleType.ANCILLARY}
                  ownerId={accessoryDetail.uniqueId}
                  buttonId={'ancillary_details-add_photo'}
                  buttonPermission={EPermission.EDIT_ANCILLARY}
                  hidden={galleryType !== EGalleryTypeAncillary.Owner}
                  init={() => setGalleryType(EGalleryTypeAncillary.Owner)}
                  onClickEdit={() =>
                    navigate(
                      `${ERouteLinks.AccessoriesEditImages.replace(
                        ':id',
                        accessoryDetail.uniqueId as string,
                      )}`,
                    )
                  }
                  onClickAdd={() =>
                    navigate(
                      `${ERouteLinks.AccessoriesAddImages.replace(
                        ':id',
                        accessoryDetail.uniqueId as string,
                      )}`,
                    )
                  }
                />

                <ImagesGallery
                  key={'ancillary_details_ART_gallery'}
                  moduleType={EModuleType.ART}
                  ownerId={accessoryDetail.artUniqueId}
                  buttonId={'ancillary_details_ART-add_photo'}
                  buttonPermission={EPermission.MANAGE_LOCAL_ART}
                  hidden={galleryType !== EGalleryTypeAncillary.ART}
                  showButton={false}
                />
              </Grid>
              <Grid
                sx={{
                  width: { xs: '100%', md: 'calc(100% - 270px)' },
                  rowGap: 1,
                  alignContent: 'flex-start',
                }}
              >
                <AccessoryInformationContainer size={{ xs: 12 }} container>
                  <Grid size={{ xs: 12 }}>
                    <Stack>
                      <AccessoryInformationTitle variant="subtitle1">
                        {t(EAccessoryLabels.accessoryGeneralInformation)}
                      </AccessoryInformationTitle>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.accessoryType)}
                      </Typography>
                      <Typography variant="body2">{accessoryDetail?.type}</Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography
                        variant="subtitle2"
                        sx={{ marginBottom: '4px', textTransform: 'uppercase' }}
                      >
                        {t(EAccessoryLabels.artId)}
                      </Typography>
                      <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                        {accessoryDetail?.artId}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Stack sx={{ width: '100%' }}>
                      {accessoryDetail.artUniqueId && (
                        <Link
                          href={ERouteLinks.ViewAncillaryDetails.replace(
                            ':id',
                            accessoryDetail.artUniqueId,
                          ).replace(':type', EArtAncillaryTypeNameLowerCase.ACCESSORY)}
                          underline="none"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ display: 'block', width: '100%' }}
                        >
                          <Button
                            variant={EButtonVariants.contained}
                            size={EButtonSizes.small}
                            label={t('accessoryDetails.showArtInformation.button')}
                            fullWidth
                          />
                        </Link>
                      )}
                    </Stack>
                  </Grid>
                  {showField('manufacturer') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAccessoryLabels.manufacturer)}
                        </Typography>
                        <Typography variant="body2">
                          {findInDictionary(accessoryDetail?.manufacturerUniqueId, [
                            ...(artAccessoryManufacturerFlashEliminator ?? []),
                            ...(artAccessoryManufacturerLaserLightModule ?? []),
                            ...(artAccessoryManufacturerMagazine ?? []),
                            ...(artAccessoryManufacturerOpticalSight ?? []),
                          ])}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('productName') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAccessoryLabels.productName)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(accessoryDetail?.productName)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('calibre') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAccessoryLabels.calibre)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(accessoryDetail?.calibre)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('hostType') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAccessoryLabels.hostType)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(accessoryDetail?.hostType)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('proofHouse') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAccessoryLabels.proofHouse)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(accessoryDetail?.proofHouse)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('composition') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAccessoryLabels.composition)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(accessoryDetail?.composition)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('magnification') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAccessoryLabels.magnification)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(accessoryDetail?.magnification)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('utility') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAccessoryLabels.utility)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(accessoryDetail?.utility)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('reticle') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAccessoryLabels.reticle)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(accessoryDetail?.reticle)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('lightcolour') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAccessoryLabels.lightcolour)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(accessoryDetail?.lightcolour)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('capacity') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EAccessoryLabels.capacity)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(accessoryDetail?.capacity)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                </AccessoryInformationContainer>
                <AccessoryInformationContainer
                  size={{ xs: 12 }}
                  container
                  borderHidden={!showMarkings}
                >
                  <Grid size={{ xs: 12 }}>
                    <Stack>
                      <AccessoryInformationTitle variant="subtitle1">
                        {t(EAccessoryLabels.accessoryDetailedInformation)}
                      </AccessoryInformationTitle>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.firecycleID)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(accessoryDetail?.fireCycleId)}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.serialNumber)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(accessoryDetail?.serialNumber)}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.componentState)}
                      </Typography>
                      <Typography variant="body2">
                        <Chips
                          text={emptyFieldHolder(accessoryDetail?.state)}
                          variant={
                            renderDataColorByValues(
                              accessoryDetail.activityUniqueId,
                              accessoryDetail.legalityUniqueId,
                              accessoryDetail.stateUniqueId,
                            ).stateField
                          }
                        />
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.exhibitNumber)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(accessoryDetail?.exhibitNumber)}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.currentOwner)}
                      </Typography>
                      <Typography variant="body2">
                        <StyledLegalLink
                          legalId={accessoryDetail.ownerId}
                          legalType={getLegalEntityType(accessoryDetail.ownerType)}
                          labelText={accessoryDetail.ownerName}
                          legalVisibilityUniqueId={accessoryDetail.ownerVisibilityUniqueId}
                        />
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.currentKeeper)}
                      </Typography>

                      <Typography variant="body2">
                        <StyledLegalLink
                          legalId={accessoryDetail.keeperId}
                          legalType={getLegalEntityType(accessoryDetail.keeperType)}
                          labelText={accessoryDetail.keeperName}
                          legalVisibilityUniqueId={accessoryDetail.keeperVisibilityUniqueId}
                        />
                      </Typography>
                    </Stack>
                  </Grid>
                  {accessoryDetail?.customFields?.map((customField) => (
                    <Grid size={{ xs: 6, lg: 3 }} key={customField.customFieldUniqueId}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {customField?.translations?.filter(
                          (translation) =>
                            selectedLanguage?.uniqueId === translation.languageUniqueId,
                        )?.[0]?.name ||
                          customField?.translations?.filter(
                            (translation) =>
                              defaultLanguage?.uniqueId === translation.languageUniqueId,
                          )?.[0]?.name}
                      </Typography>
                      <Typography variant="body2">
                        {renderCustomFieldData(customField)}
                      </Typography>
                    </Grid>
                  ))}

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EAccessoryLabels.manufacturedDate)}
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(accessoryDetail?.productionDate, shortDateFormat)}
                      </Typography>
                    </Stack>
                  </Grid>
                </AccessoryInformationContainer>
                <AccessoryInformationContainer size={{ xs: 12 }} container borderHidden>
                  {accessoryDetail.markings && showMarkings && (
                    <>
                      <Grid size={{ xs: 12 }}>
                        <Stack>
                          <AccessoryInformationTitle variant="subtitle1">
                            {t(EAccessoryLabels.accessoryMarkings)}
                          </AccessoryInformationTitle>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAccessoryLabels.importMarking)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(
                              accessoryDetail.markings.find(
                                (marking) => marking.markingType === EMarkingTypes.IMPORT,
                              )?.marking,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAccessoryLabels.appliedMarking)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(
                              accessoryDetail.markings.find(
                                (marking) => marking.markingType === EMarkingTypes.APPLIED,
                              )?.marking,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAccessoryLabels.deactivationMarking)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(
                              accessoryDetail.markings.find(
                                (marking) =>
                                  marking.markingType === EMarkingTypes.DEACTIVATION,
                              )?.marking,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAccessoryLabels.exportMarking)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(
                              accessoryDetail.markings.find(
                                (marking) => marking.markingType === EMarkingTypes.EXPORT,
                              )?.marking,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAccessoryLabels.surplusSaleMarking)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(
                              accessoryDetail.markings.find(
                                (marking) =>
                                  marking.markingType === EMarkingTypes.SURPLUS_SALE,
                              )?.marking,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAccessoryLabels.yearOfTransfer)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(accessoryDetail.yearOfTransfer)}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAccessoryLabels.confiscationMarking)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(
                              accessoryDetail.markings.find(
                                (marking) =>
                                  marking.markingType === EMarkingTypes.CONFISCATION,
                              )?.marking,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EAccessoryLabels.yearOfConfiscation)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(accessoryDetail.yearOfConfiscation)}
                          </Typography>
                        </Stack>
                      </Grid>
                    </>
                  )}
                  {accessoryDetail.markings && (
                    <Grid
                      container
                      size={{ xs: 12 }}
                      sx={{ justifyContent: 'flex-end', mt: 3 }}
                    >
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Button
                          fullWidth
                          label={
                            showMarkings
                              ? t('ancillaryDetails.hideAncillaryMarkings.button')
                              : t('ancillaryDetails.seeAncillaryMarkings.button')
                          }
                          variant={EButtonVariants.outlined}
                          size={EButtonSizes.small}
                          onClick={() => setShowMarkings(!showMarkings)}
                        />
                      </Grid>
                    </Grid>
                  )}
                </AccessoryInformationContainer>
              </Grid>
            </Grid>
          </DetailsTemplate>
          <Grid size={{ xs: 12 }}>
            <Stack sx={{ pb: 2, pt: 2 }}>
              {nextPageAlert && (
                <Box sx={{ marginBottom: '16px' }}>
                  <Alert text={nextPageAlert.text} variant={nextPageAlert.variant} />
                </Box>
              )}
              {ancillaryAlert && (
                <Box sx={{ marginBottom: '16px' }}>
                  <Alert text={ancillaryAlert.text} variant={ancillaryAlert.variant} />
                </Box>
              )}
              {transactionAlert && (
                <Box sx={{ marginBottom: '16px' }}>
                  <Alert text={transactionAlert.text} variant={transactionAlert.variant} />
                </Box>
              )}
              {hasPermissions([EPermission.VIEW_LIMITED_ANCILLARIES]) && (
                <Button
                  label={
                    isAllExpanded
                      ? t('accessoryDetails.collapseAll.button')
                      : t('accessoryDetails.expandAll.button')
                  }
                  onClick={() => setIsAllExpanded((prev) => !prev)}
                  variant={EButtonVariants.outlined}
                  sx={{ width: '150px', alignSelf: 'flex-end', mt: 2 }}
                />
              )}
            </Stack>
          </Grid>
        </Grid>
      )}
      {hasPermissions(
        [EPermission.VIEW_LIMITED_ANCILLARIES, EPermission.VIEW_ALL_ANCILLARIES],
        true,
      ) && (
        <Accordion
          title={t('accessoryDetails.transactions.header')}
          name="transactions"
          key="transactions"
          expanded={isAllExpanded}
        >
          <AccessoriesTransactions
            inGroup={
              !!accessoryDetail?.groupName &&
              ![EFirearmStateId.EXPORTED].includes(
                accessoryDetail?.stateUniqueId as EFirearmStateId,
              )
            }
            ownerId={accessoryDetail?.ownerId}
            ownerVisibilityUniqueId={accessoryDetail?.ownerVisibilityUniqueId}
            keeperId={accessoryDetail?.keeperId}
            keeperVisibilityUniqueId={accessoryDetail?.keeperVisibilityUniqueId}
            stateUniqueId={accessoryDetail?.stateUniqueId}
            hideManagement={!licenceAuthorityValid}
          />
        </Accordion>
      )}
      {hasPermissions([EPermission.MANAGE_COMMENTS_ANCILLARY]) && (
        <Accordion
          title={t('accessoryDetails.comments.header')}
          name="comments"
          key="comments"
          expanded={isAllExpanded}
        >
          <AccessoriesComments id={id} hideManagement={!licenceAuthorityValid} />
        </Accordion>
      )}
      {hasPermissions([EPermission.MANAGE_ATTACHMENT_ANCILLARY]) && (
        <Accordion
          title={t('accessoryDetails.attachments.header')}
          name="attachments"
          key="attachments"
          expanded={isAllExpanded}
        >
          <AccessoriesAttachments idAncillary={id} hideManagement={!licenceAuthorityValid} />
        </Accordion>
      )}
    </div>
  );
};

export default DetailAccessories;
